import { useState } from 'react'
import type { ReactNode } from 'react'
import { createStore, initialState, metroMapReducer } from './metroMapStore'
import type { MetroMapEvent, MetroMapState, Store } from './metroMapStore'
import { MetroMapStoreContext } from './metroMapStoreContext'

const getSystemTheme = (): 'light' | 'dark' =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

/**
 * Creates a single MetroMap store lazily on first render and provides
 * it to every descendant that needs to read state or dispatch events.
 *
 * Why context (and not a module-level singleton): tests, Storybook, and
 * visual-regression harnesses need to mount multiple independent map
 * instances within a single page. A singleton would bleed state between
 * them; a context provider keeps each instance isolated.
 *
 * Why context (and not store-in-MetroMapCanvas): the URL-as-state
 * wiring needs to dispatch events from the router layer (e.g., a
 * `/stations/<slug>` route activation must seed `selectedStation`
 * without going through a tap). That dispatch has to target the same
 * store the canvas is reading from.
 *
 * Consumer hooks live in `./useMetroMapStore` — kept in a separate
 * file so this one satisfies the `react-refresh/only-export-components`
 * ESLint rule (Fast Refresh only works on files that export
 * components exclusively).
 */
export function MetroMapStoreProvider({ children }: { children: ReactNode }) {
  const [store] = useState<Store<MetroMapState, MetroMapEvent>>(() =>
    createStore(metroMapReducer, { ...initialState, theme: getSystemTheme() }),
  )
  return <MetroMapStoreContext.Provider value={store}>{children}</MetroMapStoreContext.Provider>
}
